import { useQuery } from "@tanstack/react-query";
import { Trophy, Timer, Flame, Medal, Star, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { NameWithAvatar } from "@/components/Avatar";

const RANK = ["bg-[#FACC15] border-black", "bg-white border-black", "bg-[#FEF9C3] border-black"];

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl border-2 border-black bg-white p-4" data-testid={testId}>
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500"><Icon className="h-4 w-4" /> {label}</p>
      <p className="mt-1 font-display text-3xl font-extrabold">{value}</p>
    </div>
  );
}

const hrs = (v) => (v == null ? "—" : `${v}h`);

export function Leaderboard() {
  const { data, isLoading } = useQuery({ queryKey: ["leaderboard"], queryFn: () => api.get("/admin/leaderboard").then((r) => r.data), refetchInterval: 60000 });
  if (isLoading || !data) return <p className="text-zinc-600">Loading…</p>;
  const rows = data.rows;
  const top = rows[0];
  const fastest = [...rows].filter((r) => r.avg_resolution_hours != null).sort((a, b) => a.avg_resolution_hours - b.avg_resolution_hours)[0];

  return (
    <div className="space-y-6" data-testid="leaderboard">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat icon={Trophy} label={`Team resolved today · ${data.day}`} value={data.team_resolved_today} testId="lb-team-today" />
        <Stat icon={Timer} label="Team avg resolution (30d, working hrs)" value={hrs(data.team_avg_hours)} testId="lb-team-avg" />
        <Stat icon={Flame} label="Quickest closer (30d avg)" value={fastest ? `${fastest.name.split(" ")[0]} · ${hrs(fastest.avg_resolution_hours)}` : "—"} testId="lb-fastest" />
        <Stat icon={Star} label={`Customer rating (${data.team_ratings_count} rating${data.team_ratings_count === 1 ? "" : "s"})`} value={data.team_csat != null ? `${data.team_csat} / 5` : "—"} testId="lb-team-csat" />
        <Stat icon={RotateCcw} label="Reopened by customers (30d)" value={data.team_reopened ?? 0} testId="lb-team-reopened" />
      </div>

      {top && top.resolved_today > 0 && (
        <div className="flex flex-wrap items-center gap-4 rounded-2xl border-2 border-black bg-[#FACC15] p-5 brut-shadow fade-up" data-testid="lb-top-closer">
          <span className="grid h-14 w-14 place-items-center rounded-full border-2 border-black bg-black text-[#FACC15]"><Trophy className="h-7 w-7" /></span>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em]">Closer of the day</p>
            <p className="font-display text-2xl font-extrabold">{top.name}</p>
            <p className="text-sm">{top.resolved_today} resolved today · avg {hrs(top.avg_resolution_hours)} per ticket</p>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border-2 border-black bg-white">
        <table className="w-full text-left text-sm" data-testid="leaderboard-table">
          <thead className="bg-black text-[11px] uppercase tracking-wider text-white">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Executive</th>
              <th className="px-4 py-3">Today</th>
              <th className="px-4 py-3">7 days</th>
              <th className="px-4 py-3">30 days</th>
              <th className="px-4 py-3">Avg resolution</th>
              <th className="hidden px-4 py-3 sm:table-cell">Fastest</th>
              <th className="px-4 py-3">CSAT</th>
              <th className="px-4 py-3">Reopened</th>
              <th className="px-4 py-3">Open now</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {rows.map((r, i) => (
              <tr key={r.id} className={i === 0 && r.resolved_today > 0 ? "bg-[#FEF9C3]" : ""} data-testid={`lb-row-${r.id}`}>
                <td className="px-4 py-3">
                  <span className={`inline-grid h-8 w-8 place-items-center rounded-full border-2 font-display font-extrabold ${RANK[i] || "border-zinc-300 bg-white"}`}>{i < 3 ? <Medal className="h-4 w-4" /> : i + 1}</span>
                </td>
                <td className="px-4 py-3 font-semibold"><NameWithAvatar id={r.id} name={r.name} size={28} /> <span className="ml-1 text-[10px] uppercase text-zinc-500">{r.role}</span>{!r.active && <span className="ml-1 text-[10px] uppercase text-[#B91C1C]">inactive</span>}</td>
                <td className="px-4 py-3 font-display text-xl font-extrabold" data-testid={`lb-today-${r.id}`}>{r.resolved_today}</td>
                <td className="px-4 py-3 font-mono">{r.resolved_7d}</td>
                <td className="px-4 py-3 font-mono">{r.resolved_30d}</td>
                <td className="px-4 py-3 font-mono" data-testid={`lb-avg-${r.id}`}>{hrs(r.avg_resolution_hours)}</td>
                <td className="hidden px-4 py-3 font-mono sm:table-cell">{hrs(r.fastest_hours)}</td>
                <td className="px-4 py-3 font-mono" data-testid={`lb-csat-${r.id}`}>
                  {r.avg_rating != null ? <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-black" />{r.avg_rating} <span className="text-xs text-zinc-500">({r.ratings_count})</span></span> : "—"}
                </td>
                <td className="px-4 py-3 font-mono" data-testid={`lb-reopened-${r.id}`}>
                  {r.reopened ? <span className="inline-flex items-center gap-1 font-bold text-[#C2410C]"><RotateCcw className="h-3.5 w-3.5" />{r.reopened} <span className="text-xs font-normal text-zinc-500">({r.reopen_rate}%)</span></span> : <span className="text-zinc-400">0</span>}
                </td>
                <td className="px-4 py-3 font-mono">{r.open_assigned}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500">Resolution time counts working hours only (Mon–Sat 10:00–19:00 IST by default). "Today" resets at midnight IST.</p>
    </div>
  );
}
